import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button as Buttons, Divider, Icon, Spinner, StackView, TextInput, Tooltip, Typography } from '@telus-uds/components-web';
import ManagedDatePicker from '../../../common/ManagedDatePicker';
import Calendar from '@telus-uds/palette-allium/build/web/icons/Calendar';
import EyeOpened from '@telus-uds/palette-allium/build/web/icons/EyeOpened';
import Edit from '@telus-uds/palette-allium/build/web/icons/Edit';
import Delete from '@telus-uds/palette-allium/build/web/icons/Delete';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import ReactSelect from 'react-select';
import request from '../../../utils/request';
import { useLocalizedStringsContext } from '../../../context/LocalizedStringsContext';
import { useLocaleContext } from '../../../context/LocaleContext';
import ConfirmationBox from '../../../design/shared/ConfirmationBox';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import {
  FormContainer,
  SelectedChip,
  FieldOptionsGroup,
  ResponsiveTableContainer,
  BilingualFieldContainer,
  PaginationWrapper,
  PaginationContainer,
  PageButtons,
  ActivePage,
  NaviagationButtons,
  NaviagationButtonText
} from './css';
import CouponDetailsModal from './CouponDetailsModal';
import NotificationBanner from './NotificationBanner';
import ErrorMessage from '../../../errorMessage';

// --- Helpers and constants ---

const FIELD_DEFS = [
  // Configuration fields
  { name: 'customerSegment', label: 'FIELDS.CUSTOMER_SEGMENT', type: 'multiselect', options: 'CUSTOMER_SEGMENT' },
  { name: 'contractTerm', label: 'FIELDS.CONTRACT_TERM', type: 'multiselect', options: 'CONTRACT_TERM' },
  { name: 'businessConnectEdition', label: 'FIELDS.BUSINESS_CONNECT_EDITION', type: 'multiselect', options: 'BUSINESS_CONNECT_EDITION' },
  { name: 'doesTheCustomerRequireWhiteGlove', label: 'FIELDS.WHITE_GLOVE_REQUIRED', type: 'multiselect', options: 'WHITE_GLOVE_REQUIRED' },
  { name: 'typeOfInstallation', label: 'FIELDS.INSTALLATION_TYPE', type: 'multiselect', options: 'INSTALLATION_TYPE' },
  { name: 'provinceOfTheOrder', label: 'FIELDS.PROVINCE', type: 'multiselect', options: 'PROVINCE' },
  { name: 'multisiteOrder', label: 'FIELDS.MULTISITE_ORDER', type: 'multiselect', options: 'MULTISITE_ORDER' },
  { name: 'otherTelusServices', label: 'FIELDS.OTHER_TELUS_SERVICES', type: 'multiselect', options: 'OTHER_TELUS_SERVICES' },
  { name: 'minimumSeats', label: 'FIELDS.MINIMUM_SEATS', type: 'number' },
  // Options loaded from the API; values stored as comma-separated IDs
  { name: 'channel', label: 'FIELDS.CHANNEL', type: 'multiselect', source: 'channels' },
  { name: 'dealership', label: 'FIELDS.DEALERSHIP', type: 'multiselect', source: 'dealerships' },
  // Coupon details
  { name: 'couponCode', label: 'FIELDS.COUPON_CODE', type: 'text', required: true },
  { name: 'couponType', label: 'FIELDS.COUPON_TYPE', type: 'select', options: 'COUPON_TYPE', required: true },
  { name: 'dollarValue', label: 'FIELDS.DOLLAR_VALUE', type: 'text', required: true },
  { name: 'duration', label: 'FIELDS.DURATION', type: 'select', options: 'DURATION', required: true },
  { name: 'startDate', label: 'FIELDS.START_DATE', type: 'date' },
  { name: 'expireDate', label: 'FIELDS.EXPIRE_DATE', type: 'date', required: true },
  { name: 'commentInContract', label: 'FIELDS.COMMENT_IN_CONTRACT', type: 'textarea' },
  // Bilingual fields
  { name: 'descriptionEn', label: 'FIELDS.DESCRIPTION_EN', type: 'bilingual', lang: 'en' },
  { name: 'descriptionFr', label: 'FIELDS.DESCRIPTION_FR', type: 'bilingual', lang: 'fr' },
  { name: 'socStarterEn', label: 'FIELDS.SOC_STARTER_EN', type: 'bilingual', lang: 'en' },
  { name: 'socStarterFr', label: 'FIELDS.SOC_STARTER_FR', type: 'bilingual', lang: 'fr' },
  { name: 'socOfficeEn', label: 'FIELDS.SOC_OFFICE_EN', type: 'bilingual', lang: 'en' },
  { name: 'socOfficeFr', label: 'FIELDS.SOC_OFFICE_FR', type: 'bilingual', lang: 'fr' }
];

const BILINGUAL_PAIRS = [
  ['descriptionEn', 'descriptionFr'],
  ['socStarterEn', 'socStarterFr'],
  ['socOfficeEn', 'socOfficeFr']
];

const getTranslation = (COUPON_CODES, keyPath) => {
  return keyPath.split('.').reduce((obj, key) => obj && obj[key], COUPON_CODES) || keyPath;
};

// Helper function to format date to show only date (no time) - only for display
const formatDateOnly = (dateValue) => {
  if (!dateValue) return '';
  return moment(dateValue).format('YYYY-MM-DD');
};

const buildInitialValues = () => {
  const vals = {};
  FIELD_DEFS.forEach(f => { vals[f.name] = ''; });
  return vals;
};

const buildValidationSchema = (isFrench) => {
  const msg = isFrench ? 'Ce champ est requis' : 'This field is required';
  const minSeatsMsg = isFrench ? 'Veuillez entrer un nombre entier positif' : 'Please enter a positive whole number';
  const couponCodeMsg = isFrench ? 'Seuls les caractères alphanumériques sont autorisés' : 'Only alphanumeric characters are allowed';
  const dollarValueMsg = isFrench ? 'Veuillez entrer un nombre valide' : 'Please enter a valid number';
  const shape = {};
  FIELD_DEFS.forEach(f => {
    if (f.required) {
      if (f.type === 'text' || f.type === 'textarea' || f.type === 'select' || f.type === 'date') {
        shape[f.name] = Yup.string().required(msg);
      }
    }
    if (f.name === 'minimumSeats') {
      shape[f.name] = Yup.string()
        .matches(/^\d*$/, minSeatsMsg); // Allow empty string, only validate format if value is provided
    }
    if (f.name === 'couponCode') {
      shape[f.name] = Yup.string()
        .required(msg)
        .matches(/^[a-zA-Z0-9]+$/, couponCodeMsg);
    }
    if (f.name === 'dollarValue') {
      shape[f.name] = Yup.string()
        .required(msg)
        .matches(/^-?(?:\d+|\d*\.\d+)$/, dollarValueMsg)
        .test('is-number', dollarValueMsg, val => {
          if (val === undefined || val === null) return false;
          const num = Number(val);
          return !Number.isNaN(num);
        });
    }
  });
  return Yup.object().shape(shape);
};

const getOptionList = (field, isFrench) => {
  if (!field.options) return [];
  const en = EN.COUPON_CODES.FIELD_OPTIONS[field.options];
  const fr = FR.COUPON_CODES.FIELD_OPTIONS[field.options];
  if (!en || !fr) return [];
  return Object.keys(en).map(key => ({
    value: key,
    label: isFrench ? fr[key] : en[key],
    data: { en: en[key], fr: fr[key] }
  }));
};

const toBilingualString = (selected, options) => {
  if (!selected) return '';
  if (Array.isArray(selected)) {
    return selected.map(opt => `${opt.data.en}, ${opt.data.fr}`).join(', ');
  }
  return `${selected.data.en}, ${selected.data.fr}`;
};

const fromBilingualString = (str, options) => {
  if (!str) return [];
  const arr = str.split(', ');
  const result = [];
  for (let i = 0; i < arr.length; i += 2) {
    const en = arr[i], fr = arr[i + 1];
    const opt = options.find(o => o.data.en === en && o.data.fr === fr);
    if (opt) result.push(opt);
  }
  return result;
};

const displayBilingual = (str, isFrench) => {
  if (!str) return '';
  const arr = str.split(', ');
  let out = [];
  for (let i = 0; i < arr.length; i += 2) {
    if (arr[i] && arr[i + 1]) out.push(isFrench ? arr[i + 1] : arr[i]);
  }
  return out.join(', ');
};

const toIdList = (selected) => (selected || []).map(opt => opt.value).join(',');

const fromIdList = (str, options) => {
  if (!str) return [];
  const ids = String(str).split(',').map(id => id.trim()).filter(Boolean);
  return options.filter(opt => ids.includes(opt.value));
};

const fetchReferenceOptions = async () => {
  const [channelsResult, dealershipsResult] = await Promise.allSettled([
    request.get('/channels'),
    request.get('/dealerships')
  ]);
  const channels = channelsResult.status === 'fulfilled'
    ? ((channelsResult.value && channelsResult.value.data && channelsResult.value.data.channels) || [])
    : [];
  const dealerships = dealershipsResult.status === 'fulfilled'
    ? ((dealershipsResult.value && dealershipsResult.value.data && dealershipsResult.value.data.rows) || [])
    : [];
  return {
    channels: channels.map(c => ({ value: String(c.id), label: c.description || c.name })),
    dealerships: dealerships
      .map(d => ({ value: String(d.id), label: d.name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  };
};

// --- Main component ---

const CouponCodes = () => {
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewingId, setViewingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: '', data: null });
  const [notificationRef, setNotificationRef] = useState(null);
  const [referenceOptions, setReferenceOptions] = useState({ channels: [], dealerships: [] });
  const localizedStrings = useLocalizedStringsContext();
  const { COUPON_CODES } = localizedStrings;
  const locale = useLocaleContext();
  const isFrench = locale === 'fr-CA';

  // Pagination constants
  const COUPONS_PER_PAGE = 20;

  // Notification messages
  const notify = (action, type = 'success') => {
    const msg = {
      success: {
        save: isFrench ? 'Coupon sauvegardé avec succès!' : 'Coupon saved successfully!',
        update: isFrench ? 'Coupon mis à jour avec succès!' : 'Coupon updated successfully!',
        delete: isFrench ? 'Coupon supprimé avec succès!' : 'Coupon deleted successfully!'
      },
      error: {
        save: isFrench ? 'Erreur lors de la sauvegarde du coupon. Veuillez réessayer.' : 'Error saving coupon. Please try again.',
        update: isFrench ? 'Erreur lors de la mise à jour du coupon. Veuillez réessayer.' : 'Error updating coupon. Please try again.',
        delete: isFrench ? 'Erreur lors de la suppression du coupon. Veuillez réessayer.' : 'Error deleting coupon. Please try again.',
        notFound: isFrench ? 'Coupon non trouvé pour la mise à jour' : 'Coupon not found for update',
        validation: isFrench ? 'Veuillez remplir au moins un champ' : 'Please fill at least one field',
        duplicate: isFrench ? 'Ce code de coupon existe déjà.' : 'This coupon code already exists.'
      }
    };
    setNotification({ message: msg[type][action], type });
  };

  // Helper function to extract coupon data from inconsistent API structure
  const extractCouponData = (coupon) => {
    const couponData = coupon.coupon || coupon;
    const answersData = coupon.answers || {};
    
    return {
      id: coupon.id || Date.now() + Math.random(),
      couponCode: couponData.couponCode || '',
      dollarValue: couponData.dollarValue || '',
      duration: couponData.duration || '',
      startDate: couponData.startDate || '',
      expireDate: couponData.expireDate || '',
      commentInContract: couponData.commentInContract || '',
      descriptionEn: couponData.descInstallEn || '',
      descriptionFr: couponData.descInstallFr || '',
      socStarterEn: couponData.socStarterEn || '',
      socStarterFr: couponData.socStarterFr || '',
      socOfficeEn: couponData.socOfficeEn || '',
      socOfficeFr: couponData.socOfficeFr || '',
      couponType: couponData.couponType || '',
      customerSegment: answersData.customerSegment || '',
      contractTerm: answersData.contractTerm || '',
      businessConnectEdition: answersData.businessConnectEdition || '',
      doesTheCustomerRequireWhiteGlove: answersData.doesTheCustomerRequireWhiteGlove || '',
      typeOfInstallation: answersData.typeOfInstallation || '',
      provinceOfTheOrder: answersData.provinceOfTheOrder || '',
      multisiteOrder: answersData.multisiteOrder || '',
      otherTelusServices: answersData.otherTelusServices || '',
      minimumSeats: answersData.minimumSeats || '',
      channel: answersData.channel || '',
      dealership: answersData.dealership || '',
      createdAt: coupon.createdAt || coupon.created_at || new Date().toISOString(),
      updatedAt: coupon.updatedAt || coupon.updated_at || coupon.createdAt || coupon.created_at || new Date().toISOString(),
      apiResponse: coupon
    };
  };

  // Fetch coupons
  const fetchCoupons = async () => {
    try {
      setLoading(true);
      setLoadingMessage(isFrench ? 'Chargement des coupons...' : 'Loading Coupons...');
      const response = await request.get('/orderCoupons');
      let coupons = response.data;
      if (coupons && coupons.data && Array.isArray(coupons.data)) coupons = coupons.data;
      else if (coupons && coupons.coupons && Array.isArray(coupons.coupons)) coupons = coupons.coupons;
      else if (coupons && coupons.results && Array.isArray(coupons.results)) coupons = coupons.results;
      if (!Array.isArray(coupons)) { setSaved([]); return; }
      setSaved(
        coupons.map(extractCouponData)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      );
    } catch {
      setSaved([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);
  useEffect(() => {
    let isMounted = true;
    fetchReferenceOptions().then(options => { if (isMounted) setReferenceOptions(options); });
    return () => { isMounted = false; };
  }, []);
  useEffect(() => { if (notification) { const t = setTimeout(() => setNotification(null), 3000); return () => clearTimeout(t); } }, [notification]);

  // --- Form logic ---

  const initialFormValues = useMemo(() => buildInitialValues(), []);
  const validationSchema = useMemo(() => buildValidationSchema(isFrench), [isFrench]);

  const getEditInitialValues = (combo) => {
    if (!combo) return initialFormValues;
    const vals = { ...initialFormValues };
    FIELD_DEFS.forEach(f => { vals[f.name] = combo[f.name] || ''; });
    return vals;
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    if (editingId) {
      try {
        setLoading(true);
        setLoadingMessage(isFrench ? 'Mise à jour du coupon...' : 'Updating Coupon...');
        const combo = saved.find(c => c.id === editingId);
        if (!combo) { notify('notFound', 'error'); return; }
        const currentTimestamp = new Date().toISOString();
        const payload = {
          coupon: {
            couponCode: values.couponCode,
            dollarValue: values.dollarValue,
            duration: values.duration,
            startDate: values.startDate,
            expireDate: values.expireDate,
            commentInContract: values.commentInContract,
            couponType: values.couponType,
            descInstallEn: values.descriptionEn,
            descInstallFr: values.descriptionFr,
            socStarterEn: values.socStarterEn,
            socStarterFr: values.socStarterFr,
            socOfficeEn: values.socOfficeEn,
            socOfficeFr: values.socOfficeFr
          },
          answers: {
            customerSegment: values.customerSegment,
            contractTerm: values.contractTerm,
            businessConnectEdition: values.businessConnectEdition,
            doesTheCustomerRequireWhiteGlove: values.doesTheCustomerRequireWhiteGlove,
            typeOfInstallation: values.typeOfInstallation,
            provinceOfTheOrder: values.provinceOfTheOrder,
            multisiteOrder: values.multisiteOrder,
            otherTelusServices: values.otherTelusServices,
            minimumSeats: values.minimumSeats,
            channel: values.channel,
            dealership: values.dealership
          },
          createdAt: combo.createdAt,
          updatedAt: currentTimestamp,
          createMissingQuestions: false,
          replaceAll: true
        };
        await request.put(`/orderCoupons/${editingId}`, payload);
        notify('update', 'success');
        setEditingId(null);
        resetForm();
        fetchCoupons();
      } catch {
        notify('update', 'error');
      } finally {
        setLoading(false);
      }
      return;
    }
    // Create
    if (!Object.values(values).some(v => v !== '' && v !== null && v !== undefined)) {
      notify('validation', 'error');
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage(isFrench ? 'Sauvegarde du coupon...' : 'Saving Coupon...');
      const currentTimestamp = new Date().toISOString();
      // Check for duplicate coupon code before saving
      const duplicate = saved.some(
        c => c.couponCode.trim().toLowerCase() === (values.couponCode || '').trim().toLowerCase()
      );
      if (duplicate) {
        notify('duplicate', 'error');
        setTimeout(() => {
          if (notificationRef && notificationRef.scrollIntoView) {
            notificationRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        setLoading(false);
        return;
      }
      const payload = {
        coupon: {
          couponCode: values.couponCode || '',
          descInstallEn: values.descriptionEn || '',
          descInstallFr: values.descriptionFr || '',
          socStarterEn: values.socStarterEn || '',
          socStarterFr: values.socStarterFr || '',
          socOfficeEn: values.socOfficeEn || '',
          socOfficeFr: values.socOfficeFr || '',
          dollarValue: values.dollarValue || '',
          duration: values.duration || '',
          commentInContract: values.commentInContract || '',
          startDate: values.startDate || '',
          expireDate: values.expireDate || '',
          couponType: values.couponType || ''
        },
        answers: {
          customerSegment: values.customerSegment || '',
          contractTerm: values.contractTerm || '',
          businessConnectEdition: values.businessConnectEdition || '',
          doesTheCustomerRequireWhiteGlove: values.doesTheCustomerRequireWhiteGlove || '',
          typeOfInstallation: values.typeOfInstallation || '',
          provinceOfTheOrder: values.provinceOfTheOrder || '',
          multisiteOrder: values.multisiteOrder || '',
          otherTelusServices: values.otherTelusServices || '',
          minimumSeats: values.minimumSeats || '',
          channel: values.channel || '',
          dealership: values.dealership || ''
        },
        createdAt: currentTimestamp,
        updatedAt: currentTimestamp,
        createMissingQuestions: false
      };
      const response = await request.post('/orderCoupons', payload);
      setSaved(prev => [{ id: response.data.id || Date.now(), ...values, createdAt: new Date().toISOString(), apiResponse: response.data }, ...prev]);
      resetForm();
      notify('save', 'success');
    } catch {
      notify('save', 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- Table logic ---

  const tableFields = [
    'couponCode', 'dollarValue', 'duration', 'couponType', 'expireDate', 'commentInContract'
  ];
  const tableHeaders = tableFields.map(f =>
    getTranslation(COUPON_CODES, (FIELD_DEFS.find(fd => fd.name === f) && FIELD_DEFS.find(fd => fd.name === f).label) || f)
  );

  // --- Pagination logic ---

  const totalCoupons = saved.length;
  const totalPages = Math.ceil(totalCoupons / COUPONS_PER_PAGE);
  const startIndex = (currentPage - 1) * COUPONS_PER_PAGE;
  const endIndex = startIndex + COUPONS_PER_PAGE;
  const currentCoupons = saved.slice(startIndex, endIndex);

  // Reset to page 1 when coupons change
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // --- Pagination component ---

  const Pagination = () => {
    if (totalPages <= 1) return null;

    const maxPages = 5;

    const getPages = () => {
      let startPage;
      let endPage;

      if (totalPages <= maxPages) {
        startPage = 1;
        endPage = totalPages;
      } else {
        const maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
        const maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
        if (currentPage <= maxPagesBeforeCurrentPage) {
          startPage = 1;
          endPage = maxPages;
        } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
          startPage = totalPages - maxPages + 1;
          endPage = totalPages;
        } else {
          startPage = currentPage - maxPagesBeforeCurrentPage;
          endPage = currentPage + maxPagesAfterCurrentPage;
        }
      }

      const pages = Array.from(Array(endPage + 1 - startPage).keys()).map(
        i => startPage + i
      );
      return pages;
    };

    return (
      <PaginationWrapper>
        <PaginationContainer>
          {currentPage > 1 && (
            <NaviagationButtons
              onClick={() => {
                setCurrentPage(currentPage - 1);
              }}
            >
              <NaviagationButtonText>«</NaviagationButtonText>
            </NaviagationButtons>
          )}

          {getPages().map(pageNumber =>
            pageNumber === currentPage ? (
              <ActivePage
                key={pageNumber}
                onClick={() => {
                  setCurrentPage(pageNumber);
                }}
              >
                {pageNumber}
              </ActivePage>
            ) : (
              <PageButtons
                key={pageNumber}
                onClick={() => {
                  setCurrentPage(pageNumber);
                }}
              >
                {pageNumber}
              </PageButtons>
            )
          )}
          {currentPage < totalPages && (
            <NaviagationButtons
              onClick={() => {
                setCurrentPage(currentPage + 1);
              }}
            >
              <NaviagationButtonText>»</NaviagationButtonText>
            </NaviagationButtons>
          )}
        </PaginationContainer>
      </PaginationWrapper>
    );
  };

  // --- Render helpers ---

  const renderField = (field, formik) => {
    const { values, setFieldValue, errors, touched } = formik;
    const val = values[field.name];
    // --- Add input restrictions for minimumSeats, couponCode, dollarValue ---
    const handleInput = (value) => {
      // UDS TextInput passes value directly, not an event object
      if (field.name === 'minimumSeats') {
        // Only allow digits, never allow minus or dot, allow empty string
        value = value.replace(/[^0-9]/g, '');
      }
      if (field.name === 'couponCode') {
        // Only allow alphanumeric
        value = value.replace(/[^a-zA-Z0-9]/g, '');
      }
      if (field.name === 'dollarValue') {
        // Allow optional leading minus, digits and single dot
        value = value.replace(/[^\d.-]/g, '');
        const isNegative = value.startsWith('-');
        // Strip extra minus signs
        value = (isNegative ? '-' : '') + value.slice(isNegative ? 1 : 0).replace(/-/g, '');
        // Remove extra dots
        const parts = value.split('.');
        if (parts.length > 2) value = parts.shift() + '.' + parts.join('');
        // Normalise leading dot cases
        if (value === '-') {
          setFieldValue(field.name, value);
          return;
        }
        if (value.startsWith('-.')) value = value.replace('-.', '-0.');
        if (!isNegative && value.startsWith('.')) value = value.replace('.', '0.');
      }
      setFieldValue(field.name, value);
    };
    if (field.type === 'text' || field.type === 'number') {
      return (
        <div>
          <TextInput
            name={field.name}
            label={getTranslation(COUPON_CODES, field.label) + (field.required ? ' *' : '')}
            type="text"
            min={undefined}
            value={val || ''}
            onChange={handleInput}
            placeholder={`${getTranslation(COUPON_CODES, 'PLACEHOLDERS.ENTER')} ${getTranslation(COUPON_CODES, field.label).toLowerCase()}...`}
            autoComplete="off"
            validation={errors[field.name] && touched[field.name] ? 'error' : undefined}
          />
          {errors[field.name] && touched[field.name] && (
            <ErrorMessage type="text" text={errors[field.name]} />
          )}
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <div>
          <label style={{ fontWeight: 600 }}>{getTranslation(COUPON_CODES, field.label) + (field.required ? ' *' : '')}</label>
          <textarea
            name={field.name}
            value={val || ''}
            onChange={e => setFieldValue(field.name, e.target.value)}
            placeholder={`${getTranslation(COUPON_CODES, 'PLACEHOLDERS.ENTER')} ${getTranslation(COUPON_CODES, field.label).toLowerCase()}...`}
            style={{
              width: '100%', minHeight: '100px', padding: '12px',
              border: errors[field.name] && touched[field.name] ? '1px solid #d32f2f' : '1px solid #ccc',
              borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit'
            }}
          />
          {errors[field.name] && touched[field.name] && (
            <ErrorMessage type="text" text={errors[field.name]} />
          )}
        </div>
      );
    }
    if (field.type === 'date') {
      return (
        <div onFocus={() => window.dispatchEvent(new Event('scroll'))}>
          <ManagedDatePicker
            id={`coupon-date-${field.name}`}
            label={getTranslation(COUPON_CODES, field.label) + (field.required ? ' *' : '')}
            hint={`${getTranslation(COUPON_CODES, 'PLACEHOLDERS.SELECT')} ${getTranslation(COUPON_CODES, field.label).toLowerCase()}...`}
            value={val || ''}
            onValueChange={v => setFieldValue(field.name, v)}
            isDayDisabled={() => false}
            dateFormat="YYYY/MM/DD"
            initialVisibleMonth={moment().format('YYYY-MM-DD')}
            copy={isFrench ? 'fr' : 'en'}
            validation={errors[field.name] && touched[field.name] ? 'error' : undefined}
          />
          {errors[field.name] && touched[field.name] && (
            <ErrorMessage type="text" text={errors[field.name]} />
          )}
        </div>
      );
    }
    if (field.type === 'select') {
      const options = getOptionList(field, isFrench);
      return (
        <div>
          <div style={{ fontWeight: 600 }}>{getTranslation(COUPON_CODES, field.label) + (field.required ? ' *' : '')}</div>
          <ReactSelect
            data-name={field.name}
            value={fromBilingualString(val, options)[0] || null}
            onChange={opt => setFieldValue(field.name, opt ? toBilingualString(opt, options) : '')}
            options={options}
            placeholder={`Select ${getTranslation(COUPON_CODES, field.label).toLowerCase()}...`}
            isClearable
            menuPortalTarget={document.body}
            styles={{ control: provided => ({ ...provided, backgroundColor: 'white', borderColor: errors[field.name] && touched[field.name] ? '#d32f2f' : provided.borderColor }), menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
          {errors[field.name] && touched[field.name] && (
            <ErrorMessage type="text" text={errors[field.name]} />
          )}
        </div>
      );
    }
    if (field.type === 'multiselect') {
      const isReferenceField = !!field.source;
      const options = isReferenceField ? referenceOptions[field.source] : getOptionList(field, isFrench);
      return (
        <div>
          <div style={{ fontWeight: 600 }}>{getTranslation(COUPON_CODES, field.label)}</div>
          <ReactSelect
            data-name={field.name}
            value={isReferenceField ? fromIdList(val, options) : fromBilingualString(val, options)}
            onChange={opts => {
              if (isReferenceField) {
                setFieldValue(field.name, toIdList(opts));
                return;
              }
              setFieldValue(field.name, opts ? toBilingualString(opts, options) : '');
            }}
            options={options}
            isMulti
            closeMenuOnSelect={false}
            placeholder={`Select ${getTranslation(COUPON_CODES, field.label).toLowerCase()}...`}
            menuPortalTarget={document.body}
            styles={{ control: provided => ({ ...provided, backgroundColor: 'white', borderColor: errors[field.name] && touched[field.name] ? '#d32f2f' : provided.borderColor }), menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
          {errors[field.name] && touched[field.name] && (
            <ErrorMessage type="text" text={errors[field.name]} />
          )}
        </div>
      );
    }
    // Bilingual fields handled in pairs below
    return null;
  };

  // --- Render ---

  // Show loading spinner while coupons are being fetched
  if (loading) {
    return <Spinner fullScreen label={loadingMessage || (isFrench ? 'Chargement des coupons...' : 'Loading Coupons...')} show />;
  }

  return (
    <>
    <StackView space={4}>
      <Typography heading="h2" variant={{ size: 'h2' }} block>{getTranslation(COUPON_CODES, 'TITLE')}</Typography>
      <FormContainer data-form-section>
        <StackView space={4}>
          <Typography heading="h3" variant={{ size: 'h3' }} block>{editingId ? 'Update Coupon' : 'Add Coupon'}</Typography>
          <Divider space={0} variant={{ inverse: true }} />
          <Formik
            initialValues={editingId ? getEditInitialValues(saved.find(c => c.id === editingId)) : initialFormValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleFormSubmit}
          >
            {formik => {
              // Scroll to first error field after submit (fix: scroll to any field, not just couponCode)
              React.useEffect(() => {
                if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
                  // Try to scroll to the first field with error that is visible in the DOM
                  const firstErrorField = Object.keys(formik.errors).find(fieldName => {
                    // Try input, textarea, and select
                    return (
                      document.querySelector(`[name="${fieldName}"]`) ||
                      document.querySelector(`[id="${fieldName}-textarea"]`) ||
                      document.querySelector(`[id="${fieldName}-date"]`)
                    );
                  });
                  let el =
                    document.querySelector(`[name="${firstErrorField}"]`) ||
                    document.querySelector(`[id="${firstErrorField}-textarea"]`) ||
                    document.querySelector(`[id="${firstErrorField}-date"]`);
                  if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.focus();
                  }
                }
              }, [formik.submitCount, formik.errors]);
              return (
                <Form>
                  <StackView space={4}>
                    {/* Configuration fields */}
                    <StackView space={3}>
                      <Typography heading="h4" variant={{ size: 'h4' }} block>{getTranslation(COUPON_CODES, 'CONFIGURATION_FIELDS')}</Typography>
                      <FieldOptionsGroup>
                        {FIELD_DEFS.filter(f => f.type === 'multiselect' || f.type === 'number').map(f => (
                          <div key={f.name}>{renderField(f, formik)}</div>
                        ))}
                      </FieldOptionsGroup>
                    </StackView>
                    <Divider />
                    {/* Coupon details */}
                    <StackView space={3}>
                      <Typography heading="h4" variant={{ size: 'h4' }} block>{getTranslation(COUPON_CODES, 'COUPON_DETAILS')}</Typography>
                      <FieldOptionsGroup>
                        {FIELD_DEFS.filter(f => ['text', 'textarea', 'select', 'date'].includes(f.type) && !f.name.startsWith('description') && !f.name.startsWith('soc')).map(f => (
                          <div key={f.name}>{renderField(f, formik)}</div>
                        ))}
                      </FieldOptionsGroup>
                    </StackView>
                    {/* Bilingual fields */}
                    <StackView space={3}>
                      <Typography heading="h4" variant={{ size: 'h4' }} block>{getTranslation(COUPON_CODES, 'BILINGUAL_CONTENT')}</Typography>
                      {BILINGUAL_PAIRS.map(([en, fr]) => (
                        <BilingualFieldContainer key={en}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <TextInput
                              name={en}
                              label={getTranslation(COUPON_CODES, FIELD_DEFS.find(f => f.name === en).label) + ' (English)'}
                              value={formik.values[en] || ''}
                              onChange={(value) => formik.setFieldValue(en, value)}
                              placeholder={`${getTranslation(COUPON_CODES, 'PLACEHOLDERS.ENTER')} ${getTranslation(COUPON_CODES, FIELD_DEFS.find(f => f.name === en).label).toLowerCase()}...`}
                            />
                            <TextInput
                              name={fr}
                              label={getTranslation(COUPON_CODES, FIELD_DEFS.find(f => f.name === fr).label) + ' (French)'}
                              value={formik.values[fr] || ''}
                              onChange={(value) => formik.setFieldValue(fr, value)}
                              placeholder={`${getTranslation(COUPON_CODES, 'PLACEHOLDERS.ENTER')} ${getTranslation(COUPON_CODES, FIELD_DEFS.find(f => f.name === fr).label).toLowerCase()}...`}
                            />
                          </div>
                        </BilingualFieldContainer>
                      ))}
                    </StackView>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <Buttons 
                        type="submit" 
                        variant={{ priority: 'high' }}
                        onPress={(e) => {
                          // Let the form submit naturally, but ensure formik handles it
                          if (e) {
                            formik.handleSubmit(e);
                          }
                        }}
                      >
                        {editingId ? 'Update Coupon' : 'Add Coupon'}
                      </Buttons>
                      {editingId ? (
                        <Buttons variant={{ priority: 'low' }} onPress={() => setEditingId(null)}>{getTranslation(COUPON_CODES, 'CANCEL')}</Buttons>
                      ) : (
                        <Buttons variant={{ priority: 'low' }} onPress={formik.resetForm}>{getTranslation(COUPON_CODES, 'RESET_FORM')}</Buttons>
                      )}
                    </div>
                  </StackView>
                </Form>
              );
            }}
          </Formik>
        </StackView>
      </FormContainer>
      <Divider space={2} variant={{ inverse: true }} />
      <NotificationBanner
        notification={notification}
        onClose={() => setNotification(null)}
        ref={el => setNotificationRef(el)}
      />
       {/* Table */}
       <StackView space={3}>
         <ResponsiveTableContainer>
           <div className="responsive-table-container">
             <table className="responsive-table">
               <caption className="responsive-table__caption">
                 {getTranslation(COUPON_CODES, 'SAVED_COMBINATIONS')} ({totalCoupons})
               </caption>
               <thead className="responsive-table__thead">
                 <tr>
                   {tableHeaders.map(header => <th key={header}>{header}</th>)}
                   <th>{getTranslation(COUPON_CODES, 'ACTIONS')}</th>
                 </tr>
               </thead>
               <tbody className="responsive-table__tbody">
                 {currentCoupons.map(combo => (
                   <tr key={combo.id}>
                     {tableFields.map(f => (
                       <td key={f} data-header-label={getTranslation(COUPON_CODES, (FIELD_DEFS.find(fd => fd.name === f) && FIELD_DEFS.find(fd => fd.name === f).label) || f)}>
                         {(FIELD_DEFS.find(fd => fd.name === f) && FIELD_DEFS.find(fd => fd.name === f).type) === 'multiselect'
                           ? <SelectedChip>{displayBilingual(combo[f], isFrench)}</SelectedChip>
                           : (FIELD_DEFS.find(fd => fd.name === f) && FIELD_DEFS.find(fd => fd.name === f).type) === 'select'
                           ? displayBilingual(combo[f], isFrench)
                           : (FIELD_DEFS.find(fd => fd.name === f) && FIELD_DEFS.find(fd => fd.name === f).type) === 'date'
                           ? formatDateOnly(combo[f]) || '-'
                           : combo[f] || '-'}
                       </td>
                     ))}
                     <td>
                       <div className="responsive-table__actions">
                         <Tooltip content={getTranslation(COUPON_CODES, 'VIEW')}>
                           <Buttons
                             icon={EyeOpened}
                             accessibilityLabel={getTranslation(COUPON_CODES, 'VIEW')}
                             onPress={() => setViewingId(combo.id)}
                             variant={{ text: true }}
                           />
                         </Tooltip>
                         <Tooltip content={getTranslation(COUPON_CODES, 'EDIT')}>
                           <Buttons
                             icon={Edit}
                             accessibilityLabel={getTranslation(COUPON_CODES, 'EDIT')}
                             onPress={() => { setEditingId(combo.id); const formSection = document.querySelector('[data-form-section]'); if (formSection) { formSection.scrollIntoView({ behavior: 'smooth', block: 'start' }); } }}
                             variant={{ text: true }}
                           />
                         </Tooltip>
                         <Tooltip content={getTranslation(COUPON_CODES, 'DELETE')}>
                           <Buttons
                             icon={Delete}
                             accessibilityLabel={getTranslation(COUPON_CODES, 'DELETE')}
                             onPress={() => setConfirmModal({ open: true, type: 'delete', data: combo.id })}
                             variant={{ text: true }}
                           />
                         </Tooltip>
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
         </ResponsiveTableContainer>
         <Pagination />
       </StackView>
      {/* Confirmation Modal */}
      <ConfirmationBox
        open={confirmModal.open}
        setOpen={() => setConfirmModal({ open: false, type: '', data: null })}
        header={isFrench ? 'Confirmation' : 'Confirmation'}
        hideCloseButton={false}
      >
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ marginBottom: '20px', fontSize: '16px' }}>
            {confirmModal.type === 'delete'
              ? (isFrench ? 'Êtes-vous sûr de vouloir supprimer ce coupon?' : 'Are you sure you want to delete this coupon?')
              : ''}
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <Buttons
              variant={{ priority: 'high', semantic: 'danger' }}
               onPress={async () => {
                 const id = confirmModal.data;
                 try {
                   setLoading(true);
                   setLoadingMessage(isFrench ? 'Suppression du coupon...' : 'Deleting coupon...');
                   const combo = saved.find(c => c.id === id);
                  if (combo && combo.apiResponse && combo.apiResponse.id) await request.delete(`/orderCoupons/${combo.apiResponse.id}`);
                  setSaved(prev => prev.filter(c => c.id !== id));
                  setConfirmModal({ open: false, type: '', data: null });
                  notify('delete', 'success');
                } catch {
                  setConfirmModal({ open: false, type: '', data: null });
                  notify('delete', 'error');
                } finally {
                  setLoading(false);
                }
              }}
            >
              {isFrench ? 'Confirmer' : 'Confirm'}
            </Buttons>
            <Buttons variant={{ priority: 'low' }} onPress={() => setConfirmModal({ open: false, type: '', data: null })}>
              {isFrench ? 'Annuler' : 'Cancel'}
            </Buttons>
          </div>
        </div>
      </ConfirmationBox>
    </StackView>
      {/* Detailed View Modal - Rendered outside Box to ensure proper portal rendering */}
      <CouponDetailsModal
        isOpen={!!viewingId}
        combination={saved.find(c => c.id === viewingId)}
        referenceOptions={referenceOptions}
        onClose={() => setViewingId(null)}
      />
    </>
  );
};

export default CouponCodes;
